import { useMemo, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./components/ThemeProvider";
import { Sidebar } from "./components/Sidebar";
import { Topbar, Content } from "./components/Topbar";
import { NewItemModal } from "./components/NewItemModal";
import { TodayView } from "./views/TodayView";
import { BoardView } from "./views/BoardView";
import { ListView, TableView } from "./views/ListTableViews";
import { ItemDetailView } from "./views/ItemDetailView";
import { AdminView } from "./views/AdminView";
import { useRealtime, type RealtimeStatus } from "./lib/realtime";
import type { TenantBranding } from "./lib/types";
import type { ThemePreference } from "./lib/theme";

// Bootstrap: in production the SPA reads its context (tenant, user, server-side theme preference,
// tenants.branding) from a bootstrap payload injected at load. Until that endpoint lands we
// derive the tenant slug from the host and fall back to product defaults — the SAME single build
// serves every tenant, branding applied at runtime. Wiring the real bootstrap is a one-line swap
// of this function for a fetch.
interface Bootstrap {
  tenantSlug: string;
  tenantName: string;
  userName: string;
  userRole: string;
  themePreference: ThemePreference | null;
  branding: TenantBranding | null;
}

function readBootstrap(): Bootstrap {
  const injected = (window as unknown as { __AC_BOOTSTRAP__?: Partial<Bootstrap> }).__AC_BOOTSTRAP__ ?? {};
  const host = location.hostname;
  const slug = host.split(".")[0] ?? "metanoia";
  return {
    tenantSlug: injected.tenantSlug ?? slug,
    tenantName: injected.tenantName ?? capitalise(slug),
    userName: injected.userName ?? "You",
    userRole: injected.userRole ?? "Member",
    themePreference: injected.themePreference ?? null,
    branding: injected.branding ?? null,
  };
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const TITLES: Record<string, string> = {
  "/today": "Today",
  "/board": "Board",
  "/list": "List",
  "/table": "Table",
  "/tickets": "Tickets",
  "/admin": "Settings",
};

export default function App() {
  const qc = useMemo(
    () =>
      new QueryClient({
        defaultOptions: { queries: { staleTime: 10_000, refetchOnWindowFocus: false, retry: 1 } },
      }),
    [],
  );
  const boot = useMemo(readBootstrap, []);

  return (
    <QueryClientProvider client={qc}>
      <ThemeProvider serverPreference={boot.themePreference} branding={boot.branding}>
        <BrowserRouter>
          <Shell boot={boot} qc={qc} />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function Shell({ boot, qc }: { boot: Bootstrap; qc: QueryClient }) {
  const [rt, setRt] = useState<RealtimeStatus>({ connected: false, lastEventId: 0 });
  const [showNew, setShowNew] = useState(false);
  useRealtime(qc, setRt);

  const path = useLocation().pathname;
  const title = TITLES[path] ?? (path.startsWith("/item/") ? "Detail" : "Action Center");
  const crumb = `${boot.tenantSlug}.tasks.littlebee.ai`;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "236px 1fr", minHeight: "100vh", position: "relative", zIndex: 1 }}>
      <Sidebar tenantName={boot.tenantName} userName={boot.userName} userRole={boot.userRole} />
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Topbar crumb={crumb} title={title} realtime={rt} onNew={() => setShowNew(true)} />
        <Content>
          <Routes>
            <Route path="/" element={<Navigate to="/today" replace />} />
            <Route path="/today" element={<TodayView />} />
            <Route path="/board" element={<BoardView kind="task" />} />
            <Route path="/list" element={<ListView />} />
            <Route path="/table" element={<TableView />} />
            <Route path="/tickets" element={<BoardView kind="ticket" />} />
            <Route path="/item/:id" element={<ItemDetailView />} />
            <Route path="/admin" element={<AdminView />} />
            <Route path="*" element={<Navigate to="/today" replace />} />
          </Routes>
        </Content>
      </div>
      {showNew && <NewItemModal onClose={() => setShowNew(false)} />}
    </div>
  );
}
